/**
 * Filter Processor - Core filter transformation and processing
 *
 * Transforms user-defined filters into QueryFilter and Relationship structures
 * for graph database queries. Handles special cases for different entity types
 * and field-specific logic.
 *
 * @module queryBuilderCore/processors/filterProcessor
 */

import { surfaceFilters } from '@/app/constant';
import { transformFilterOperator } from '@/sections/insights/queryBuilder/legacy/transformers/operatorTransformer';
import { transformFilterValue } from '@/sections/insights/queryBuilder/legacy/transformers/valueTransformer';
import {
  Filter,
  LogicalOperator,
  QueryFilter,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/types';
import { createAttributeRelationships } from '@/sections/insights/queryBuilder/legacy/utils/attributeRelationships';
import { createPortRelationships } from '@/sections/insights/queryBuilder/legacy/utils/portRelationships';
import { expandChariotGroupInSourceValues } from '@/sections/insights/queryBuilder/legacy/utils/sourceExpansion';
import { convertAssetStatusFilterToQueryFilter } from '@/sections/insights/queryBuilder/legacy/utils/statusConversion';
import { convertRelativeTimeToTimestamp } from '@/sections/insights/queryBuilder/legacy/utils/timeTransformer';
import { buildAssetStatusFilter } from '@/utils/entities/assetStatusQuery.util';
import {
  getFieldType,
  getMappedFieldName,
  getMappedOperator,
  getStatusOperator,
  mapStatusValue,
} from '@/utils/mappings.util';

/**
 * Processes a single filter into its graph query representation
 *
 * @param filter - Filter to process
 * @param entityType - Type of entity being filtered
 * @param groupLogicalOperator - Logical operator for the filter group
 * @param assetFilterOptions - Available asset filter options
 * @param vulnFilterOptions - Available vulnerability filter options
 * @param technologyFilterOptions - Available technology filter options
 * @param seedFilterOptions - Available seed filter options
 * @param integrationValues - List of integration source values
 * @param queryContentTab - Current query content tab context
 * @returns Processed filter components (direct filters, relationships, etc.)
 */
export function processFilter(
  filter: Filter,
  entityType: string,
  groupLogicalOperator: LogicalOperator,
  assetFilterOptions?: Record<string, unknown>,
  vulnFilterOptions?: Record<string, unknown>,
  technologyFilterOptions?: Record<string, unknown>,
  seedFilterOptions?: Record<string, unknown>,
  integrationValues?: string[],
  _queryContentTab?: string
): {
  directFilter?: QueryFilter;
  relationships?: Relationship[];
  isNot?: boolean;
} {
  if (!filter.attribute) return {};

  const field = getMappedFieldName(entityType, filter.attribute);
  const originalOperator = filter.operator || '';
  let operator = getMappedOperator(originalOperator);
  let value: string | string[] | number | number[] | boolean | boolean[] =
    Array.isArray(filter.value) ? filter.value : filter.value || '';
  let isNot = false;

  if (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'value' in value[0]
  ) {
    const extractedValues = (
      value as unknown as Array<{ value: string | number | boolean }>
    ).map(item => item.value);

    if (extractedValues.every(v => typeof v === 'string')) {
      value = extractedValues as string[];
    } else if (extractedValues.every(v => typeof v === 'number')) {
      value = extractedValues as number[];
    } else if (extractedValues.every(v => typeof v === 'boolean')) {
      value = extractedValues as boolean[];
    } else {
      value = extractedValues.map(v => String(v));
    }
  }

  if (operator === '!=') {
    operator = '=';
    isNot = true;
  } else if (operator === 'NOT CONTAINS') {
    operator = 'CONTAINS';
    isNot = true;
  } else if (operator === 'NOT IN') {
    operator = 'IN';
    isNot = true;
  }

  if (field === 'source' && (assetFilterOptions || vulnFilterOptions)) {
    const expandedValues = expandChariotGroupInSourceValues(
      value as string | string[],
      entityType,
      assetFilterOptions,
      vulnFilterOptions,
      integrationValues
    );
    value = expandedValues;

    if (entityType === 'Asset') {
      const sourceValues = Array.isArray(value)
        ? value.map(v => String(v))
        : [String(value)];

      let sourceMatchFilter: QueryFilter;
      if (sourceValues.length > 1) {
        const filters: QueryFilter[] = [];
        for (const src of sourceValues) {
          filters.push({
            field: '',
            operator: 'OR',
            value: [
              { field: 'class', operator: '=', value: src, not: false },
              { field: 'dns', operator: '=', value: src, not: false },
            ],
          });
        }
        sourceMatchFilter = {
          field: '',
          operator: 'OR',
          value: filters,
          not: isNot,
        };
      } else {
        sourceMatchFilter = {
          field: '',
          operator: 'OR',
          value: [
            {
              field: 'class',
              operator: '=',
              value: sourceValues[0],
              not: false,
            },
            { field: 'dns', operator: '=', value: sourceValues[0], not: false },
          ],
          not: isNot,
        };
      }

      const sourceFilter: QueryFilter = {
        field: '',
        operator: 'OR',
        value: [
          { field: 'source', operator: '=', value: 'account', not: false },
          { field: 'source', operator: '=', value: 'seed', not: false },
        ],
        not: false,
      };

      return {
        relationships: [
          {
            label: 'DISCOVERED',
            length: -1,
            select_one: true,
            source: {
              labels: ['Asset'],
              filters: [sourceFilter, sourceMatchFilter],
            },
          },
        ],
        isNot: false,
      };
    }
  }

  if (field === 'visited' && operator === 'within') {
    return {
      relationships: [
        {
          label: 'HAS_ATTRIBUTE',
          select_one: true,
          target: {
            labels: ['Attribute'],
            filters: [
              {
                field: 'name',
                operator: '=',
                value: 'new',
                not: false,
              },
              {
                field: 'value',
                operator: '=',
                value: ['asset'],
                not: isNot,
              },
            ],
          },
        },
      ],
      isNot: false,
    };
  }

  if (field === 'created' && originalOperator === 'in the last') {
    const timestamp = convertRelativeTimeToTimestamp(value as string);
    if (timestamp) {
      return {
        directFilter: {
          field: field,
          operator: '>=',
          value: timestamp,
          not: isNot,
        },
        isNot: false,
      };
    }
  }

  if (field === 'tags') {
    const tagValues = Array.isArray(value)
      ? value.map(v => String(v))
      : [String(value)];

    const tagFilters = tagValues.map(tagValue => ({
      field: 'tags',
      operator: 'IN',
      value: tagValue,
      not: isNot,
      reverseOperands: true,
    }));

    return {
      directFilter:
        tagFilters.length === 1
          ? tagFilters[0]
          : {
              field: '',
              operator: 'OR',
              value: tagFilters,
              not: false,
            },
      isNot: false,
    };
  }

  if (field === 'status' && entityType === 'Asset') {
    const statusValues = Array.isArray(value)
      ? value.map(v => String(v))
      : [String(value)];

    const assetStatusFilter = buildAssetStatusFilter(statusValues, false);
    if (assetStatusFilter) {
      const convertedFilter = convertAssetStatusFilterToQueryFilter(
        assetStatusFilter,
        isNot
      );
      return {
        directFilter: convertedFilter,
      };
    }

    value = mapStatusValue(statusValues, 'Asset') as string | string[];
    operator = getStatusOperator(operator, Array.isArray(value));
  } else {
    value = transformFilterValue(field, value, entityType);
    operator = transformFilterOperator(field, operator, entityType, value);
  }

  if (
    field === 'source' &&
    Array.isArray(value) &&
    value.length > 1 &&
    operator === '='
  ) {
    operator = 'IN';
  }

  const fieldType = getFieldType(entityType, filter.attribute);

  if (
    fieldType === 'direct' &&
    operator === 'IN' &&
    !['origins', 'class'].includes(field) &&
    (!Array.isArray(value) || value.length <= 1)
  ) {
    operator = '=';
  }

  if (field === 'attackSurface') {
    const attackSurfaceValues = Array.isArray(value) ? value : [value];
    const surfaceFilterStructures = attackSurfaceValues
      .map(val => surfaceFilters[String(val) as keyof typeof surfaceFilters])
      .filter(Boolean);

    if (surfaceFilterStructures.length === 0) {
      return {};
    }

    if (surfaceFilterStructures.length === 1) {
      return {
        directFilter: surfaceFilterStructures[0] as QueryFilter,
        isNot,
      };
    } else {
      return {
        directFilter: {
          field: '',
          operator: 'OR',
          value: surfaceFilterStructures,
          not: isNot,
        } as QueryFilter,
        isNot,
      };
    }
  }

  if (field === 'adObjectType') {
    const adTypes = Array.isArray(value)
      ? value.map(v => String(v)).filter(type => type.trim() !== '')
      : [String(value)].filter(type => type.trim() !== '');

    if (adTypes.length === 0) {
      return {};
    }

    return {
      directFilter: {
        field: 'adObjectType',
        operator: adTypes.length === 1 ? '=' : 'IN',
        value: adTypes.length === 1 ? adTypes[0] : adTypes,
        not: isNot,
      },
      isNot,
    };
  }

  if (
    [
      'admincount',
      'isaclprotected',
      'pwdneverexpires',
      'isdc',
      'unconstraineddelegation',
      'ldapavailable',
      'ldapsavailable',
      'authenticationenabled',
      'adminsdholderprotected',
      'blocksinheritance',
      'dontreqpreauth',
      'passwordexpired',
      'encryptedtextpwdallowed',
      'smartcardrequired',
      'trustedtoauth',
      'enrolleesuppliessubject',
      'requiresmanagerapproval',
      'sensitive',
    ].includes(field)
  ) {
    if (Array.isArray(value)) {
      const hasNoValue = value.some(v => String(v) === 'false');
      const hasTrueValue = value.some(v => String(v) === 'true');

      if (hasNoValue && !hasTrueValue) {
        return {
          directFilter: {
            field: field,
            operator: '=',
            value: true,
            not: true,
          },
          isNot: false,
        };
      } else if (hasTrueValue && !hasNoValue) {
        return {
          directFilter: {
            field: field,
            operator: operator,
            value: true,
            not: isNot,
          },
          isNot: false,
        };
      } else {
        const booleanValue = value.map(v => String(v) === 'true');
        return {
          directFilter: {
            field: field,
            operator: operator,
            value: booleanValue,
            not: isNot,
          },
          isNot: false,
        };
      }
    } else {
      if (String(value) === 'false') {
        if (isNot) {
          return {
            directFilter: {
              field: field,
              operator: '=',
              value: false,
              not: true,
            },
            isNot: false,
          };
        } else {
          return {
            directFilter: {
              field: field,
              operator: '=',
              value: true,
              not: true,
            },
            isNot: false,
          };
        }
      } else {
        if (isNot) {
          return {
            directFilter: {
              field: field,
              operator: '=',
              value: true,
              not: true,
            },
            isNot: false,
          };
        } else {
          return {
            directFilter: {
              field: field,
              operator: operator,
              value: true,
              not: false,
            },
            isNot: false,
          };
        }
      }
    }
  }

  // Handle port and protocol fields with HAS_PORT relationships
  if (field === 'port' || field === 'protocol') {
    const portRelationships = createPortRelationships(
      field,
      operator,
      value,
      groupLogicalOperator,
      isNot
    );

    return {
      relationships: portRelationships,
      isNot,
    };
  }

  if (fieldType === 'direct') {
    if ((operator === 'IN' || operator === 'NOT IN') && !Array.isArray(value)) {
      value = [String(value)];
    }

    if (
      operator === 'IN' &&
      Array.isArray(value) &&
      !['origins', 'class'].includes(field)
    ) {
      if (value.length === 0) {
        return {};
      }

      const wrappedFilters: QueryFilter[] = value.map(singleValue => ({
        field,
        operator: '=',
        value: singleValue,
        not: isNot,
      }));

      const groupOperator: 'OR' | 'AND' = isNot ? 'AND' : 'OR';

      return {
        directFilter: {
          field: '',
          operator: groupOperator,
          value: wrappedFilters,
        },
        isNot,
      };
    }

    const directFilter: QueryFilter = {
      field: field,
      operator: operator,
      value: value,
    };

    if (field === 'origins') {
      if (operator === '=') {
        directFilter.operator = 'IN';
        directFilter.reverseOperands = true;
      } else if (operator === '!=' || operator === 'NOT IN') {
        directFilter.operator = 'NOT IN';
        directFilter.reverseOperands = true;
      } else if (operator === 'IN') {
        directFilter.reverseOperands = true;
      }
    }

    if (field === 'class' && operator === 'IN' && Array.isArray(value)) {
      // Class filter needs array wrapped in array for Neo4j query format
      directFilter.value = [value] as unknown as
        | string
        | string[]
        | number
        | number[]
        | boolean
        | boolean[]
        | QueryFilter[];
      directFilter.reverseOperands = false;
    }

    return {
      directFilter,
      isNot,
    };
  } else {
    return {
      relationships: createAttributeRelationships(
        field,
        operator,
        value,
        groupLogicalOperator,
        isNot
      ),
      isNot,
    };
  }
}
